import React from "react";
import { Check, Download, MoreVertical, Share2, Smartphone, X } from "lucide-react";

const DISMISS_KEY = "ap_install_prompt_dismissed_at";
const INSTALLED_KEY = "ap_install_confirmed";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator?.standalone === true
  );
}

function userAgent() {
  if (typeof navigator === "undefined") return "";
  return String(navigator.userAgent || "");
}

function isIosDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = userAgent();
  return /iPhone|iPad|iPod/i.test(ua)
    || (navigator.platform === "MacIntel" && Number(navigator.maxTouchPoints) > 1);
}

function isAndroidDevice() {
  return /Android/i.test(userAgent());
}

function isSamsungInternet() {
  return /SamsungBrowser/i.test(userAgent());
}

function readInstalledConfirmation() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberInstalled() {
  try {
    window.localStorage.setItem(INSTALLED_KEY, "1");
    window.localStorage.removeItem(DISMISS_KEY);
  } catch {
    // Storage can be blocked; standalone detection still prevents prompts in the installed app.
  }
}

function readDismissed() {
  if (typeof window === "undefined") return false;
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY));
    if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return false;
    return Date.now() - dismissedAt < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function rememberDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Storage can be blocked in privacy modes; dismissal still works in-memory.
  }
}

function Step({ number, children }) {
  return (
    <li className="flex gap-3">
      <span
        className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0E2A47] text-xs font-bold text-white"
        aria-hidden="true"
      >
        {number}
      </span>
      <span className="leading-5">{children}</span>
    </li>
  );
}

export default function InstallAppPrompt({ eligible = true, hasBottomNav = false, inline = false, alwaysAvailable = false }) {
  const standaloneAtStart = isStandalone();
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showManualHelp, setShowManualHelp] = React.useState(false);
  const [installed, setInstalled] = React.useState(
    () => standaloneAtStart || readInstalledConfirmation(),
  );
  const [dismissed, setDismissed] = React.useState(() => readDismissed());

  const ios = isIosDevice();
  const android = isAndroidDevice();
  const samsung = isSamsungInternet();

  React.useEffect(() => {
    if (standaloneAtStart) rememberInstalled();
  }, [standaloneAtStart]);

  React.useEffect(() => {
    if (!eligible || installed) return undefined;

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onInstalled = () => {
      rememberInstalled();
      setInstalled(true);
      setDeferredPrompt(null);
      setShowManualHelp(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [eligible, installed]);

  const dismiss = () => {
    setDismissed(true);
    setShowManualHelp(false);
    rememberDismissed();
  };

  const confirmInstalled = () => {
    rememberInstalled();
    setInstalled(true);
    setShowManualHelp(false);
  };

  const install = async () => {
    if (deferredPrompt?.prompt) {
      try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === "accepted") {
          rememberInstalled();
          setInstalled(true);
        }
      } catch {
        // Browser-owned install UI can fail or be withdrawn; manual guidance remains available.
        setShowManualHelp(true);
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }

    if (ios || android) setShowManualHelp(true);
  };

  const visible = eligible
    && !standaloneAtStart
    && (alwaysAvailable || (!installed && !dismissed))
    && (Boolean(deferredPrompt) || ios || android);

  if (!visible) return null;

  const installLabel = deferredPrompt
    ? "Install Arctic Plate"
    : ios
      ? "Show iPhone install steps"
      : samsung
        ? "Show Samsung install steps"
        : "Show Android install steps";

  const helpTitle = ios
    ? "Install on iPhone or iPad"
    : samsung
      ? "Install on Samsung"
      : "Install on Android";

  return (
    <aside
      data-testid="install-app-prompt"
      className={`${inline ? "relative mt-3" : "fixed left-3 right-3 z-[60]"} mx-auto max-w-md rounded-2xl border border-[#d9c9b8] bg-[#fffaf4]/98 p-4 shadow-[0_18px_45px_-18px_rgba(14,42,71,0.45)] backdrop-blur ${!inline && hasBottomNav ? "bottom-[calc(5.5rem+env(safe-area-inset-bottom))]" : !inline ? "bottom-[calc(1rem+env(safe-area-inset-bottom))]" : ""}`}
      aria-label="Install Arctic Plate"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full text-[#33475B] hover:bg-[#F1E4D6]"
        aria-label="Not now"
      >
        <X size={18} />
      </button>

      <div className="flex gap-3 pr-8">
        <img
          src="/arctic-plate-logo.webp"
          alt=""
          aria-hidden="true"
          className="h-12 w-12 shrink-0 rounded-xl object-contain"
        />
        <div className="min-w-0">
          <div className="font-semibold text-[#0E2A47]">Install Arctic Plate</div>
          <p className="mt-1 text-sm leading-5 text-[#33475B]">
            Put Arctic Plate on your Home Screen for fast access. Free — no App Store purchase required.
          </p>
        </div>
      </div>

      {showManualHelp ? (
        <div
          className="mt-3 rounded-xl bg-[#F1E4D6] px-3 py-3 text-sm text-[#0E2A47]"
          aria-live="polite"
          data-testid="install-manual-help"
        >
          <div className="flex items-center gap-2 font-bold">
            {ios ? <Share2 size={17} aria-hidden="true" /> : samsung ? <Smartphone size={17} aria-hidden="true" /> : <MoreVertical size={17} aria-hidden="true" />}
            {helpTitle}
          </div>

          {ios ? (
            <ol className="mt-3 space-y-2.5">
              <Step number="1">In <strong>Safari</strong>, tap the <strong>Share</strong> button — the square with the arrow pointing up.</Step>
              <Step number="2">Tap <strong>Add to Home Screen</strong>.</Step>
              <Step number="3">Leave <strong>Open as Web App</strong> turned <strong>ON</strong>.</Step>
              <Step number="4">Tap <strong>Add</strong>, then open the new <strong>Arctic Plate</strong> icon from your Home Screen.</Step>
            </ol>
          ) : samsung ? (
            <ol className="mt-3 space-y-2.5">
              <Step number="1">Open ArcticPlate.com in <strong>Samsung Internet</strong>.</Step>
              <Step number="2">Open the browser menu, then choose <strong>Add page to</strong> or <strong>Add to Home screen</strong>.</Step>
              <Step number="3">Choose <strong>Home screen</strong> and confirm <strong>Add</strong>.</Step>
              <Step number="4">Open the new <strong>Arctic Plate</strong> icon from your Home Screen.</Step>
            </ol>
          ) : (
            <ol className="mt-3 space-y-2.5">
              <Step number="1">Open ArcticPlate.com in <strong>Chrome</strong>.</Step>
              <Step number="2">Tap the <strong>⋮</strong> browser menu.</Step>
              <Step number="3">Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</Step>
              <Step number="4">Confirm <strong>Install/Add</strong>, then open the new <strong>Arctic Plate</strong> icon.</Step>
            </ol>
          )}

          <button
            type="button"
            onClick={confirmInstalled}
            data-testid="install-confirmed-button"
            className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#16395f]"
          >
            <Check size={17} aria-hidden="true" />
            I installed Arctic Plate
          </button>

          <button
            type="button"
            onClick={() => setShowManualHelp(false)}
            className="mt-2 min-h-[40px] w-full rounded-xl px-3 py-2 text-sm font-semibold text-[#33475B] hover:bg-white/70"
          >
            Back
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={install}
            data-testid="install-app-button"
            className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#16395f]"
          >
            <Download size={17} aria-hidden="true" />
            {installLabel}
          </button>
          <button
            type="button"
            onClick={confirmInstalled}
            className="mt-1 min-h-[40px] w-full rounded-xl px-3 py-2 text-xs font-semibold text-[#526273] hover:bg-[#F1E4D6]"
          >
            Already installed? Hide this
          </button>
        </>
      )}
    </aside>
  );
}
